import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, GameController, TrendUp, TrendDown, Minus, HandHeart, Trophy, Target } from "@phosphor-icons/react";

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export default function PlayerMatchHistory({
  matchHistory, friendlyHistory = [], playerId, onLogFriendly, canLogFriendly = false,
}) {
  const [filter, setFilter] = useState("all");

  const tournamentCount = (matchHistory || []).length;
  const friendlyCount = (friendlyHistory || []).length;
  const totalCount = tournamentCount + friendlyCount;

  const combined = [
    ...(matchHistory || []).map(m => ({ ...m, _type: "tournament", _date: m.played_at || m.created_at })),
    ...(friendlyHistory || []).map(f => ({ ...f, _type: "friendly", _date: f.date_played || f.created_at })),
  ].sort((a, b) => String(b._date).localeCompare(String(a._date)));

  const rows = filter === "tournament"
    ? combined.filter(m => m._type === "tournament")
    : filter === "friendly"
      ? combined.filter(m => m._type === "friendly")
      : combined;

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Match History ({totalCount})
          </CardTitle>
          {canLogFriendly && (
            <Button size="sm" onClick={onLogFriendly} data-testid="log-friendly-btn">
              <HandHeart className="w-4 h-4 mr-1" />Log Friendly
            </Button>
          )}
        </div>
        <div className="flex gap-2 pt-2" data-testid="match-history-filters">
          <FilterChip label={`All (${totalCount})`} active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all" />
          <FilterChip label={`Tournament (${tournamentCount})`} active={filter === "tournament"} onClick={() => setFilter("tournament")} testId="filter-tournament" />
          <FilterChip label={`Friendly (${friendlyCount})`} active={filter === "friendly"} onClick={() => setFilter("friendly")} testId="filter-friendly" />
        </div>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <GameController className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>
              {filter === "friendly" ? "No friendly matches yet" :
               filter === "tournament" ? "No tournament matches yet" : "No matches yet"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.slice(0, 15).map(m =>
              m._type === "friendly"
                ? <FriendlyMatchRow key={`f-${m.id}`} match={m} playerId={playerId} />
                : <TournamentMatchRow key={`t-${m.id}`} match={m} playerId={playerId} />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FilterChip({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
        active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"
      }`}
    >{label}</button>
  );
}

function _resultBadge(match, playerId) {
  /** Returns {label, variant, color, className} for the result badge.
   *  Completed/walkover → W / L / D (draw).
   *  Scheduled / live / pending_validation → contextual non-result label. */
  const status = match.status;
  const winnerId = match.winner_id;
  if (status === "completed" || status === "walkover") {
    if (!winnerId) return { label: "D", variant: "outline", className: "border-muted text-muted-foreground" };
    return winnerId === playerId
      ? { label: "W", variant: "default", className: "" }
      : { label: "L", variant: "destructive", className: "" };
  }
  if (status === "live") {
    return { label: "LIVE", variant: "outline", className: "border-secondary/60 text-secondary animate-pulse" };
  }
  if (status === "pending_validation") {
    return { label: "?", variant: "outline", className: "border-chart-4/50 text-chart-4" };
  }
  // scheduled / postponed / anything else unplayed
  return { label: "—", variant: "outline", className: "border-border text-muted-foreground" };
}

function _ladderArrow(match, playerId) {
  /** Returns {label, className, Icon} for the ladder movement badge:
   *  - green up arrow when the player improved (rank number went down)
   *  - red down arrow when the player dropped
   *  - yellow minus when rank unchanged
   *  - null when we don't have rank snapshots (historical match before this was tracked) */
  const isP1 = match.player1_id === playerId;
  const rankBefore = isP1 ? match.player1_rank_before : match.player2_rank_before;
  const rankAfter = isP1 ? match.player1_rank_after : match.player2_rank_after;
  if (rankBefore == null || rankAfter == null) return null;
  const change = Number(rankBefore) - Number(rankAfter);
  if (change > 0) return { label: `+${change}`, className: "text-primary border-primary/40 bg-primary/10", Icon: TrendUp, title: `Moved up from #${rankBefore} to #${rankAfter}` };
  if (change < 0) return { label: `${change}`, className: "text-destructive border-destructive/40 bg-destructive/10", Icon: TrendDown, title: `Dropped from #${rankBefore} to #${rankAfter}` };
  return { label: "0", className: "text-chart-4 border-chart-4/40 bg-chart-4/10", Icon: Minus, title: `Held position at #${rankAfter}` };
}

function TournamentMatchRow({ match, playerId }) {
  const status = match.status;
  const isUnplayed = status !== "completed" && status !== "walkover";
  const isPlayer1 = match.player1_id === playerId;
  const opponentName = isPlayer1 ? match.player2_name : match.player1_name;
  const playerScore = isPlayer1 ? match.player1_score : match.player2_score;
  const opponentScore = isPlayer1 ? match.player2_score : match.player1_score;
  const eloAfter = isPlayer1 ? match.player1_elo_after : match.player2_elo_after;
  const eloBefore = isPlayer1 ? match.player1_elo_before : match.player2_elo_before;
  const eloChange = eloAfter && eloBefore ? eloAfter - eloBefore : null;
  const matchPoints = match.my_match_points;

  const badge = _resultBadge(match, playerId);
  const isWinner = badge.label === "W";
  const ladder = !isUnplayed ? _ladderArrow(match, playerId) : null;

  // Visual tone: unplayed matches should feel neutral, not a loss.
  const containerClass = isUnplayed
    ? "border-border bg-muted/10"
    : isWinner
    ? "border-primary/30 bg-primary/5"
    : badge.label === "D"
    ? "border-border bg-muted/10"
    : "border-destructive/30 bg-destructive/5";

  // Date line: scheduled matches should show "scheduled for …", completed show played_at.
  const dateLabel = isUnplayed
    ? (match.scheduled_date
        ? `Scheduled ${formatDate(match.scheduled_date)}${match.scheduled_time ? ` · ${match.scheduled_time}` : ""}`
        : "Not yet scheduled")
    : formatDate(match.played_at || match.created_at);

  return (
    <div
      className={`rounded-lg border ${containerClass}`}
      data-testid={`match-history-${match.id}`}
    >
      <div className="flex items-center gap-4 p-4">
        <Badge
          variant={badge.variant}
          className={`w-14 justify-center ${badge.className}`}
          data-testid={`match-result-${match.id}`}
        >
          {badge.label}
        </Badge>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <Trophy className="w-3 h-3 text-muted-foreground shrink-0" />
            <p className="font-medium truncate">vs {opponentName}</p>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {match.tournament_name} · {match.round?.replace(/_/g, " ")}
          </p>
        </div>
        <div className="text-right shrink-0">
          {/* Score only for played matches — scheduled matches don't have a score yet. */}
          {isUnplayed ? (
            <p className="text-xs text-muted-foreground">
              {match.table_name ? `Table: ${match.table_name}` : "Score pending"}
            </p>
          ) : (
            <p className="font-heading font-bold">{playerScore} - {opponentScore}</p>
          )}
          <p className="text-xs text-muted-foreground">{dateLabel}</p>
        </div>
      </div>

      {/* Post-match performance strip — ELO, match-points, and ladder arrow.
          Rendered only for played matches that actually have performance data.
          Unplayed rows keep the clean single-line layout. */}
      {!isUnplayed && (eloChange !== null || matchPoints != null || ladder) && (
        <div
          className="flex items-center gap-3 px-4 py-2 border-t border-border/60 bg-background/40 text-xs"
          data-testid={`match-performance-${match.id}`}
        >
          {eloChange !== null && (
            <div className="flex items-center gap-1" data-testid={`perf-elo-${match.id}`}>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">ELO</span>
              <span className={`font-heading font-bold tabular-nums ${
                eloChange > 0 ? "text-primary" : eloChange < 0 ? "text-destructive" : "text-muted-foreground"
              }`}>
                {eloChange > 0 ? "+" : ""}{Number(eloChange).toFixed(1)}
              </span>
            </div>
          )}

          {matchPoints != null && (
            <div className="flex items-center gap-1" data-testid={`perf-points-${match.id}`}>
              <Target className="w-3 h-3 text-secondary" weight="fill" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">MP</span>
              <span className="font-heading font-bold tabular-nums text-secondary">
                {isWinner ? "+" : ""}{matchPoints}
              </span>
            </div>
          )}

          {ladder && (
            <div className="flex items-center gap-1 ml-auto" title={ladder.title} data-testid={`perf-ladder-${match.id}`}>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Rank</span>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-bold tabular-nums ${ladder.className}`}>
                <ladder.Icon className="w-3 h-3" weight="bold" />
                {ladder.label}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FriendlyMatchRow({ match, playerId }) {
  const isWinner = match.winner_id === playerId;
  const isPlayer1 = match.player1_id === playerId;
  const opponentName = isPlayer1 ? match.player2_name : match.player1_name;
  const playerScore = isPlayer1 ? match.player1_score : match.player2_score;
  const opponentScore = isPlayer1 ? match.player2_score : match.player1_score;

  return (
    <div
      className={`flex items-center gap-4 p-4 rounded-lg border-dashed border-2 ${
        isWinner ? 'border-primary/30 bg-primary/5' : 'border-muted bg-muted/10'
      }`}
      data-testid={`friendly-match-${match.id}`}
    >
      <Badge variant="outline" className="w-12 justify-center border-secondary/40 text-secondary">
        {isWinner ? 'W' : 'L'}
      </Badge>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <HandHeart className="w-3 h-3 text-secondary shrink-0" weight="fill" />
          <p className="font-medium truncate">vs {opponentName}</p>
          <Badge variant="outline" className="text-[9px] border-secondary/30 text-secondary shrink-0">Friendly</Badge>
        </div>
        <p className="text-xs text-muted-foreground truncate">
          {match.venue || "No venue recorded"}{match.notes ? ` · ${match.notes}` : ""}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="font-heading font-bold">{playerScore} - {opponentScore}</p>
        <p className="text-xs text-muted-foreground">{formatDate(match.date_played || match.created_at)}</p>
      </div>
    </div>
  );
}
